import { ipaUrl } from '@/constants';

export const POSTGREST_BASE_URL = `${ipaUrl}/app`;

const PostgrestFSMEndpoints = {
  PLAYER_SESSIONS: 'player_sessions',
  LISTENER_DATA: '/episodes_listeners',
  FIND_PLAYER_SESSION: '/rpc/find_player_session_by_episode_id',
  START_PLAYER_SESSION: 'rpc/start_player_session_v2',
  PLAY_PLAYER_SESSION: '/rpc/play_player_session',
  PAUSE_PLAYER_SESSION: '/rpc/pause_player_session',
  RESUME_LAST_PLAYER_SESSION: 'rpc/resume_last_player_session',
  SET_SESSION_POSITION: 'rpc/set_position',
  SEEK_SESSION_POSITION: 'rpc/seek_position',
  ANSWER_QUIZ: '/rpc/answer_quiz_v2',
  CLAIM_PLAYER_SESSION: '/rpc/claim_player_session',
  BOOKMARK_SESSION: '/rpc/bookmark_episode',
  UNBOOKMARK_SESSION: '/rpc/unbookmark_episode',
  SUBMIT_EPISODE_FEEDBACK: '/rpc/submit_episode_feedback',
  SUBMIT_EPISODE_EVALUATION: '/rpc/submit_episode_evaluation',
  GET_USER_UI_STATES: '/rpc/get_user_ui_states',
  UPDATE_USER_UI_STATE: '/rpc/update_user_ui_state',
} as const;

export const PostgrestUnauthenticatedEndpoints = {
  LOGIN: '/rpc/login',
  REGISTER: '/rpc/register_with_credentials',
  PLAYER_CONFIG: '/rpc/player_config_v2',
  EPISODE: '/episodes_v4',
  FEATURED_EPISODES: '/featured_episodes_v4',
  TRENDING_EPISODES: '/trending_episodes_v4',
  LIBRARY_CATEGORIES: '/rpc/library_categories',
  LIBRARY_EPISODES: '/rpc/library_episodes_v2',
  EPISODE_FACETS: '/rpc/episode_facets',
  EPISODE_CATEGORIES: '/rpc/lookup_episode_library_categories',
  FIELDS_OF_STUDY: '/fields_of_study',
  LEARNING_PATHS: '/learning_paths_v3',
  RELATED_EPISODES: '/related_episodes_v3',
  REFRESH_ACCESS_TOKEN: '/rpc/refresh_session_token',
  CHAPTER: '/chapters_v2',
  QUIZ: '/quizzes_v2',
  TRAILER_URL: '/trailer_url',
  DESIGNATION_OPTIONS: '/designation_options',
  CPA_CANADA_EPISODE: '/cpa_canada_episode',
  OFFERS: '/offers',
  UPDATE_PASSWORD: '/rpc/reset_password',
  FORGOT_PASSWORD: '/rpc/forgot_password_v3',
  VALIDATE_PASSWORD_RESET_TOKEN: '/rpc/validate_password_reset_token',
  RESET_PASSWORD_WITH_TOKEN: '/rpc/reset_password_with_token',
  VALIDATE_INVITE_TOKEN: '/rpc/is_invite_token_valid',
  GET_INVITE_DETAILS: '/rpc/get_invite_details',
  GET_SSO_REDIRECT_URI: '/rpc/sso_redirect_uri',
  EPISODES_SCHEDULED_FOR_ARCHIVING: '/episodes_scheduled_for_archiving',
  EPISODE_LEARNING_PATHS: '/learning_paths_by_episode',
  ANONYMOUS_PRICING_TABLE_VALUES: '/rpc/marketing_site_pricing_table',
  DEFAULT_PROMO_CODE: '/rpc/promotion_code',
} as const;

export const PostgrestAuthenticatedEndpoints = {
  LOGOUT: '/rpc/logout',
  PROFILE: '/profile',
  USER_REGISTRATION_STATE: '/registration_state',
  USER_TAGS: '/rpc/user_tags',
  UPDATE_USER_DESIGNATIONS: '/rpc/update_designations',
  SEND_ACCOUNT_LINKING_EMAIL: '/rpc/send_activation_email',
  FINALIZE_REGISTRATION: '/rpc/finalize_registration',
  CHAPTER_URL: '/content_url',
  PLAN: '/plan_v3',
  UPDATE_PROFILE: '/rpc/update_profile',
  CREDITS: '/credits_v2',
  PRIMARY_REGION: '/primary_region',
  LINK_PLAN: '/rpc/link_member_to_user',
  ORG_PINNED_LPS: '/rpc/organization_featured_learning_paths',
  REORDER_ORG_PINNED_LPS: '/rpc/re_order_featured_learning_paths',
  PIN_LP: '/rpc/pin_global_learning_path',
  UNPIN_LP: '/rpc/unpin_global_learning_path',
  ORGANIZATION_PLAYLIST: '/organization_playlist_v3',
  USER_ASSESSMENT: '/rpc/get_user_assessment',
  SUBMIT_USER_ASSESSMENT: '/rpc/submit_user_assessment_answers',
  HAS_SUBMITTED_PURCHASE_FEEDBACK: '/rpc/has_submitted_howdyhau',
  PURCHASE_FEEDBACK_OPTIONS: '/rpc/howdyhau_options',
  SUBMIT_PURCHASE_FEEDBACK: '/rpc/submit_howdyhau_response',
  SHOW_PRODUCT_FEEDBACK: '/show_product_feedback',
  RECOMMENDED_EPISODES: '/recommended_episodes_v2',
  UPDATE_ORG_RECOMMENDED_EPISODES: '/rpc/publish_org_recommended_episodes',
  ORGANIZATION_MEMBERS: '/organization_members_v2',
  ORGANIZATION_SETTING: '/organization_settings',
  ARCHIVE_ORGANIZATION_MEMBERS: '/rpc/archive_organization_members',
  REINVITE_ORGANIZATION_MEMBERS: '/rpc/re_invite_organization_members',
  ADD_ORGANIZATION_MEMBERS: '/rpc/add_members_to_organization',
  EDIT_ORGANIZATION_MEMBERS: '/rpc/edit_organization_members',
  UPDATE_ORGANIZATION_NAME: '/rpc/update_organization_name',
  PLAN_LIMITS: '/rpc/plan_limits_v2',
  REPORTING_CONFIG: '/rpc/reporting_config',
  LISTENER_DISTRIBUTION_REPORT: '/rpc/listener_distribution_report',
  ENGAGEMENT_TREND_REPORT: '/rpc/episode_engagement_trend_report',
  TOP_LISTENERS_REPORT: '/rpc/top_listeners_report',
  TOP_EPISODES_REPORT: '/rpc/top_episodes_report',
  ORGANIZATION_MEMBERSHIP_SUMMARY: '/rpc/membership_summary_report',
  ENGAGEMENT_SUMMARY_REPORT: '/rpc/episode_engagement_summary_report',
  LISTENER_DETAIL_REPORT: '/rpc/listener_detail_report',
  LISTENER_SUMMARY_REPORT: '/rpc/listener_summary_report',
  SUBMIT_PRODUCT_FEEDBACK: '/rpc/submit_product_feedback',
  ADD_RECOVERY_EMAIL: '/rpc/add_recovery_email',
  VERIFY_RECOVERY_EMAIL: '/rpc/verify_recovery_email',
  REMOVE_RECOVERY_EMAIL: '/rpc/archive_recovery_email',
  FETCH_RECOVERY_EMAIL: '/rpc/recovery_email_status',
  START_ACCOUNT_RECOVERY: '/rpc/start_account_recovery',
  VALIDATE_VERIFICATION_CODE: '/rpc/validate_verification_code',
  COMPLETE_ACCOUNT_RECOVERY: '/rpc/complete_account_recovery',
  VERIFY_EMAIL: '/rpc/verify_email_v2',
  RESEND_CONFIRMATION_EMAIL_OAUTH: '/rpc/resend_verification_email_oauth',
  GEN_APP_LOGIN_MAGIC_LINK: '/rpc/gen_magic_link',
  SEND_FREE_TRIAL_SHARE_EMAIL: '/rpc/send_free_trial_share_email',
  SEND_EPISODE_SHARE_EMAIL: '/rpc/send_episode_share_email',
  GET_BILLING_PORTAL_LINK: '/rpc/stripe_billing_portal_link',
  STRIPE_CUSTOMER_INFO: '/has_stripe_billing_portal',
  STRIPE_PRICING_TABLE_VALUES: '/rpc/stripe_pricing_table_values',
  STRIPE_CANCEL_SUBSCRIPTION: '/rpc/stripe_cancel_b2c_user_subscription',
  CREATE_NEW_TEAM: '/rpc/create_team_trial_org',
  TRIENNIAL_SUMMARY: '/rpc/get_cpd_triennial_summary',
  BILLING_ADMIN_ORGS: '/rpc/current_billing_admin_orgs',
  ORG_BILLING_PORTAL_LINK: '/rpc/stripe_get_org_billing_portal_link',
  CREATE_PENDING_B2B_CHECKOUT:
    '/rpc/stripe_create_b2b_team_default_checkout_session',
  STRIPE_PURCHASE_STATUS: '/rpc/stripe_purchase_status',
  CREATE_CHECKOUT_SESSION: '/rpc/stripe_create_checkout_session',
  GET_CHECKOUT_SESSION_URL: '/rpc/stripe_get_checkout_session_url',
  CONTENT_PREFERENCES: '/rpc/content_preferences',
  SET_REGION_PREFERENCE: '/rpc/set_region_preference',
  GET_POC_VALUE: '/rpc/get_poc_value',
  REQUEST_REGION: '/rpc/request_country',
  ...PostgrestFSMEndpoints,
} as const;

export const PostgrestEndpoints = {
  ...PostgrestAuthenticatedEndpoints,
  ...PostgrestUnauthenticatedEndpoints,
};

export const EPISODE_CARD_REQUIRED_FIELDS = [
  'episode_id',
  'name',
  'hook',
  'is_archived',
  'total_ethics',
  'is_premium',
  'is_recorded_live',
  'cover',
  'company_id',
  'company_name',
  'company_logo',
  'duration',
  'chapters',
  'trailer_id',
  'is_video',
  'deep_link',
  'categories',
  'field_of_study_id',
  'field_of_study',
  'categories',
  'credits',
  'episode_age_tag',
  'audience_regions',
] as const;

export const EPISODE_RELEVANCE_FIELDS = ['total_boost', 'boost_details'];

export const PostgrestErrors = {
  Onboarding: {
    MEMBER_ALREADY_LINKED: 'lumiq.activation.member.already_linked',
    MEMBER_NOT_FOUND: 'lumiq.activation.member.not_found',
    MEMBER_NOT_IN_INVITED_STATE: 'lumiq.activation.member.not_in_invited_state',
    USER_ALREADY_LINKED:
      'lumiq.activation.member.already_linked_to_member_in_org',
  },
};
